import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewContainerRef } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Observable, Subject, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PartnerAccountTypes, PartnerFeatures, SessionService, SnackBarService } from "src/app/core";
import { BrandingService } from "src/app/core/services/branding.service";
import { AccountUpgradeDialogService } from "src/app/features/account";
import { EventContextService } from "src/app/features/events";
import { VisionboardSearchItem } from "../../../models";
import { VisionboardService, VisionboardWorkspaceItemsStoreService } from "../../../services";
import { VisionboardItemDetailService } from "../../../services/visionboard-item-detail.service";
import { VisionboardItemAddRemoveClickInput } from "../../models";
import { VisionboardItemAddRemoveButtonService } from "../../services";
import { ForceVisionboardItemAddRemoveButton } from "../visionboard-item-add-remove-button/visionboard-item-add-remove-button.component";
import { VisionboardItemDetailDialogComponent } from "../visionboard-item-detail-dialog/visionboard-item-detail-dialog.component";

@Component({
  selector: "app-visionboard-item-search-result-item",
  templateUrl: "./visionboard-item-search-result-item.component.html",
  styleUrls: ["./visionboard-item-search-result-item.component.scss"],
  standalone: false,
})
export class VisionboardItemSearchResultItemComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  @Input() item: VisionboardSearchItem;
  @Input() disableDrag: boolean;
  @Input() disabled: boolean;
  @Input() allowDupes: boolean;
  @Input() showUserSelected: boolean;
  @Input() forceVisionboardItemAddRemoveButton: ForceVisionboardItemAddRemoveButton;
  @Input() showLock: boolean;
  @Input() source: string;
  @Input() customMoreActionsTemplate: TemplateRef<void>;
  @Input() customTemplate: TemplateRef<void>;
  @Input() customAddRemoveTemplate: TemplateRef<void>;

  @Output() itemClicked: EventEmitter<VisionboardSearchItem>;
  @Output() itemDialogOpened: EventEmitter<boolean> = new EventEmitter<boolean>();

  showMoreActionsMenu: boolean;
  selectable: boolean;
  userSelected: boolean;
  isHovered: boolean;
  itemDetailDialogRef: MatDialogRef<VisionboardItemDetailDialogComponent>;
  isOrderable: boolean;
  hasOrderManagement: boolean;

  constructor(
    public brandingService: BrandingService,
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private visionboardService: VisionboardService,
    private snackBarService: SnackBarService,
    private _visionboardItemDetailService: VisionboardItemDetailService,
    private _visionboardItemAddRemoveButtonService: VisionboardItemAddRemoveButtonService,
    private _vcr: ViewContainerRef,
    private _matDialog: MatDialog,
    private _sessionService: SessionService,
    private _eventContextService: EventContextService,
    private _accountUpgradeDialogService: AccountUpgradeDialogService,
  ) {
    this.itemClicked = new EventEmitter<VisionboardSearchItem>();
  }

  ngOnInit(): void {
    this.showMoreActionsMenu = this.visionboardService.allowCustomItemEdit();

    this.visionboardWorkspaceItemsStoreService.items$.pipe(takeUntil(this._destroyed$)).subscribe(() => this.updateState());

    this._visionboardItemAddRemoveButtonService.addItemClicked$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: (input: VisionboardItemAddRemoveClickInput) => {
        if (input.item.variantSkuId !== this.item.variantSkuId) return;

        if (!this.allowDupes && !this.selectable) {
          this.snackBarService.show({ message: "You've already selected that item", duration: 2000, action: "Hide" });
        } else if (!this.item.placeholder) {
          let obs$: Observable<void>;

          if (this.itemDetailDialogRef && !input.bypassModalClose) {
            obs$ = this.itemDetailDialogRef.afterClosed();
            this.itemDetailDialogRef.close();
            this.itemDetailDialogRef = null;
          } else {
            obs$ = of(null);
          }

          obs$.subscribe({
            next: () => this.itemClicked.emit(this.item),
          });
        }
      },
    });

    this._visionboardItemAddRemoveButtonService.removeItemClicked$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: (input: VisionboardItemAddRemoveClickInput) => {
        if (input.item.variantSkuId !== this.item.variantSkuId) return;

        this.itemDetailDialogRef?.close();
        this.itemDetailDialogRef = null;
      },
    });

    this.isOrderable = this.item?.isOrderable();
    this.hasOrderManagement = this._eventContextService.hasOrderManagementAccess();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onClick(): void {
    if (this.item.isCustomerUploadedSku && this.showLock) {
      if (this._sessionService.isExternalPartnerUser()) {
        this.itemDialogOpened.emit(true);

        this._accountUpgradeDialogService
          .openDialog("Edit Uploaded Item - VB Search", {
            newAccountType: PartnerAccountTypes.AllAccess,
            isFeaturePrompt: true,
            partnerFeature: PartnerFeatures.CustomerVariantSkuManagement,
          })
          .afterClosed()
          .pipe(takeUntil(this._destroyed$))
          .subscribe({
            next: () => {
              this.itemDialogOpened.emit(false);
            },
          });
      }
    } else {
      this.itemDialogOpened.emit(true);
      this.itemDetailDialogRef = this._visionboardItemDetailService.showItemDetail(
        {
          item: this.item.getVisionboardItem(),
          forceVisionboardItemAddRemoveButton: this.forceVisionboardItemAddRemoveButton,
          customMoreActionsTemplate: this.customMoreActionsTemplate,
          customAddRemoveTemplate: this.customAddRemoveTemplate,
        },
        this._vcr,
        this._matDialog,
        `${this.source} - Search Result Item`,
      );

      this.itemDetailDialogRef.afterClosed().subscribe({
        next: () => {
          this.itemDetailDialogRef = null;
          this.itemDialogOpened.emit(false);
        },
      });
    }
  }

  toggleControlsHover(isHovered: boolean): void {
    this.isHovered = isHovered;
  }

  private updateState(): void {
    const userHasItem = this.visionboardWorkspaceItemsStoreService.userHasItem(this.item.variantSkuId);

    this.userSelected = this.showUserSelected && userHasItem;
    this.selectable = this.allowDupes || !userHasItem;
  }
}
